import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import { LISTING_STATUS } from '../../util/enums/LISTING_STATUS';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { ConditionToLabel } from '../../util/enums/CONDITION';

import {
  PrimaryButton,
  Button,
  SecondaryButton,
  AvatarSmall,
  H1,
  H2,
  H4,
} from '../../components';
import ConditionBox from '../ConditionBox/ConditionBox';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm'
  )
);
const BookingDatesForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(
    /* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm'
  )
);

// This defines when ModalInMobile shows content as Modal

const TODAY = new Date();

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.

  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => window.scrollTo(0, 0);
};

const dateFormattingOptions = {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
};

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(
    price,
    marketplaceCurrency,
    intl
  );
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>{formatMoney(intl, price)}</p>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    onToggleFavorites,
    currentUser,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '' } =
    publicData || {};
  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency =
    isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime =
    isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock =
    isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;
  const isReserved =
    listing.attributes.publicData?.listingStatus === LISTING_STATUS.RESERVED;
  const isDrop = listing.attributes?.metadata?.isInDrop;
  const shippingOneItem =
    listing.attributes.publicData?.shippingPriceInSubunitsOneItem;
  const shippingAdditionalItems =
    listing.attributes.publicData?.shippingPriceInSubunitsAdditionalItems;

  const isStockLoading = isPurchase && typeof currentStock === 'undefined';
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';
  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo
    .map(info => info.name)
    .includes(processName);

  const { pickupEnabled, shippingEnabled } =
    listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [
    STOCK_MULTIPLE_ITEMS,
    STOCK_INFINITE_MULTIPLE_ITEMS,
  ].includes(listingTypeConfig?.stockType);

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const favorites =
    currentUser?.attributes.profile.privateData?.favorites || [];
  const isFavorite = favorites.includes(listing.id.uuid);

  return (
    <div className={classes}>
      <div className={css.orderHeading}>
        {titleDesktop ? (
          titleDesktop
        ) : (
          <H2 className={titleClasses}>{title}</H2>
        )}
        {subTitleText ? (
          <div className={css.orderHelp}>{subTitleText}</div>
        ) : null}
        {isOutOfStock ? (
          <span className={css.price}>SOLD</span>
        ) : isReserved ? (
          <span className={css.price}>RESERVED</span>
        ) : (
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
          />
        )}
      </div>
      <div className={css.titleInfoWrapper}>
        <div className={css.titleInfo}>
          <span>
            {listing.attributes.publicData.brand &&
            listing.attributes.publicData.designer
              ? `${listing.attributes.publicData.brand} / ${listing.attributes.publicData.designer}`
              : listing.attributes.publicData.brand ||
                listing.attributes.publicData.designer}
          </span>
        </div>
        <ConditionBox
          condition={ConditionToLabel(listing.attributes.publicData.condition)}
        />
      </div>
      {showPriceMissing ? (
        <PriceMissing />
      ) : showInvalidCurrency ? (
        <InvalidCurrency />
      ) : showBookingTimeForm ? (
        <BookingTimeForm
          className={css.bookingForm}
          formId="OrderPanelBookingTimeForm"
          lineItemUnitType={lineItemUnitType}
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          dayCountAvailableForBooking={dayCountAvailableForBooking}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          monthlyTimeSlots={monthlyTimeSlots}
          onFetchTimeSlots={onFetchTimeSlots}
          startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          timeZone={timeZone}
          marketplaceName={marketplaceName}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          payoutDetailsWarning={payoutDetailsWarning}
        />
      ) : showBookingDatesForm ? (
        <BookingDatesForm
          className={css.bookingForm}
          formId="OrderPanelBookingDatesForm"
          lineItemUnitType={lineItemUnitType}
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          dayCountAvailableForBooking={dayCountAvailableForBooking}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          monthlyTimeSlots={monthlyTimeSlots}
          onFetchTimeSlots={onFetchTimeSlots}
          timeZone={timeZone}
          marketplaceName={marketplaceName}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          payoutDetailsWarning={payoutDetailsWarning}
        />
      ) : isStockLoading ? (
        <div className={css.ctaSkeleton} />
      ) : showProductOrderForm ? (
        <ProductOrderForm
          formId="OrderPanelProductOrderForm"
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          currentStock={currentStock}
          allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
          pickupEnabled={pickupEnabled && displayPickup}
          shippingEnabled={shippingEnabled && displayShipping}
          displayDeliveryMethod={displayPickup || displayShipping}
          listingId={listing.id}
          listing={listing}
          isOwnListing={isOwnListing}
          marketplaceName={marketplaceName}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          onContactUser={onContactUser}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          payoutDetailsWarning={payoutDetailsWarning}
          shippingOneItem={shippingOneItem}
          shippingAdditionalItems={shippingAdditionalItems}
        />
      ) : showInquiryForm ? (
        <InquiryWithoutPaymentForm
          formId="OrderPanelInquiryForm"
          onSubmit={onSubmit}
        />
      ) : !isKnownProcess ? (
        <p className={css.errorSidebar}>
          <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
        </p>
      ) : null}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(OrderPanel);
